import { useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { PV } from "./pivotTokens";

export interface PivotProductOption {
  product_code: string;
  product_name: string | null;
  /** `product.sequence` — the options list in catalogue order. Null sorts last. */
  sequence?: number | null;
}

/**
 * Catalogue order, the same rule the backend's default product sort uses: `sequence`
 * ascending, unsequenced SKUs last, ties by code.
 */
export const compareProductSequence = (a: PivotProductOption, b: PivotProductOption): number => {
  const sa = a.sequence ?? null;
  const sb = b.sequence ?? null;
  if (sa !== sb) {
    if (sa === null) return 1;
    if (sb === null) return -1;
    return sa - sb;
  }
  if (a.product_code === b.product_code) return 0;
  return a.product_code < b.product_code ? -1 : 1;
};

/** The backend accepts at most this many `product_code` values on `/pivot/products`. */
export const PIVOT_PRODUCT_FILTER_MAX = 200;

const optionLabel = (o: PivotProductOption) =>
  o.product_name ? `${o.product_code} · ${o.product_name}` : o.product_code;

/**
 * Multi-select product filter for the product tab (board B1): chips for what is picked,
 * type to search by name or code. The options are the products the tab already loaded for
 * the current scope, so there is no endpoint behind it — and a picked code that has since
 * dropped out of the options still shows as a chip rather than vanishing.
 */
export default function PivotProductPicker({
  options,
  value,
  onChange,
}: {
  options: PivotProductOption[];
  value: string[];
  onChange: (codes: string[]) => void;
}) {
  const selected = useMemo(() => {
    const byCode = new Map(options.map((o) => [o.product_code, o]));
    return value.map((code) => byCode.get(code) ?? { product_code: code, product_name: null });
  }, [options, value]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selected}
      onChange={(_, next) => onChange(next.map((o) => o.product_code))}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(a, b) => a.product_code === b.product_code}
      filterSelectedOptions
      getOptionDisabled={() => value.length >= PIVOT_PRODUCT_FILTER_MAX}
      limitTags={3}
      noOptionsText="ไม่พบสินค้า"
      renderValue={(items, getItemProps) =>
        items.map((o, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              {...itemProps}
              size="small"
              label={o.product_name ?? o.product_code}
              title={optionLabel(o)}
              sx={{
                bgcolor: PV.indigoTint,
                color: PV.indigoDark,
                fontWeight: 600,
                fontSize: 12.5,
                "& .MuiChip-deleteIcon": { color: PV.indigo, fontSize: 16 },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length ? "" : "ทุกสินค้า · พิมพ์ชื่อหรือรหัสสินค้า…"}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 34,
              py: "2px !important",
              fontSize: 13.5,
              bgcolor: PV.card,
              borderRadius: PV.radiusSm,
            },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: PV.line },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: PV.indigoBorder,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: PV.indigo,
            },
          }}
        />
      )}
      sx={{ minWidth: 300, maxWidth: 520, flex: "1 1 300px" }}
    />
  );
}
